import React, { useState, useEffect } from 'react';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, DollarSign, MessageSquare, Package, Bot, ShoppingBag,
  TrendingUp, Search, ChevronLeft, ChevronRight, Eye, Shield,
  Clock, Zap, Crown, ArrowLeft, Loader2, BarChart3, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FaWhatsapp, FaInstagram, FaFacebookMessenger } from 'react-icons/fa';

const StatCard = ({ icon: Icon, label, value, sub, color = "blue" }) => {
  const colors = {
    blue: "from-blue-500 to-blue-600",
    green: "from-emerald-500 to-emerald-600",
    purple: "from-violet-500 to-violet-600",
    amber: "from-amber-500 to-amber-600",
    red: "from-red-500 to-red-600",
    pink: "from-pink-500 to-pink-600"
  };
  return (
    <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-slate-500 font-medium dark:text-zinc-400">{label}</span>
        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colors[color]} flex items-center justify-center`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
      <p className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1 dark:text-zinc-400">{sub}</p>}
    </div>
  );
};

const AdminDashboard = () => {
  const { token } = useAuth();
  const navigate = React.useCallback(() => {}, []);
  
  // Use admin-specific token ONLY — no fallback to regular token
  const adminToken = localStorage.getItem('admin_token');
  const adminUsername = localStorage.getItem('admin_username') || '';
  const headers = { Authorization: `Bearer ${adminToken}` };
  
  // Redirect to admin login if no admin token
  React.useEffect(() => {
    if (!adminToken) {
      window.location.href = '/nrv-panel/login';
    }
  }, [adminToken]);

  const handleAdminLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { headers });
    } catch (e) {}
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    localStorage.removeItem('admin_name');
    window.location.href = '/nrv-panel/login';
  };
  
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [userSearch, setUserSearch] = useState('');
  const [userPlanFilter, setUserPlanFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [aiMonitoring, setAiMonitoring] = useState(null);
  const [margins, setMargins] = useState(null);
  const [marginPeriod, setMarginPeriod] = useState('month');
  const [channelAnalytics, setChannelAnalytics] = useState(null);
  const [adminOrders, setAdminOrders] = useState(null);
  const [ordersPage, setOrdersPage] = useState(1);
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [systemData, setSystemData] = useState(null);
  const [systemLogs, setSystemLogs] = useState([]);
  const [logType, setLogType] = useState('activity');
  const [contentData, setContentData] = useState(null);
  const [ticketsData, setTicketsData] = useState(null);
  const [ticketReply, setTicketReply] = useState('');
  const [replyingTicket, setReplyingTicket] = useState(null);
  const [adminReplyImage, setAdminReplyImage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tab === 'dashboard') fetchDashboard();
    if (tab === 'users') fetchUsers();
    if (tab === 'revenue') fetchRevenue();
    if (tab === 'ai') fetchAiMonitoring();
    if (tab === 'margin') fetchMargins();
    if (tab === 'channels') fetchChannelAnalytics();
    if (tab === 'orders') fetchAdminOrders();
    if (tab === 'system') { fetchSystem(); fetchLogs(); }
    if (tab === 'content') fetchContent();
    if (tab === 'tickets') fetchTickets();
  }, [tab, usersPage, userPlanFilter, ordersPage, orderStatusFilter, logType, marginPeriod]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/dashboard`, { headers });
      setDashboard(res.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        handleAdminLogout();
        return;
      }
      toast.error('Failed to load dashboard');
    } finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams({ page: usersPage, limit: 15 });
      if (userPlanFilter !== 'all') params.append('plan', userPlanFilter);
      if (userSearch) params.append('search', userSearch);
      const res = await axios.get(`${API}/admin/users?${params}`, { headers });
      setUsers(res.data.users);
      setUsersTotal(res.data.total);
    } catch (err) {
      toast.error('Failed to load users');
    }
  };

  const fetchRevenue = async () => {
    try {
      const res = await axios.get(`${API}/admin/revenue`, { headers });
      setRevenue(res.data);
    } catch (err) {
      toast.error('Failed to load revenue');
    }
  };

  const fetchAiMonitoring = async () => {
    try {
      const res = await axios.get(`${API}/admin/ai-monitoring`, { headers });
      setAiMonitoring(res.data);
    } catch (err) {
      toast.error('Failed to load AI monitoring');
    }
  };

  const fetchMargins = async () => {
    try {
      const res = await axios.get(`${API}/admin/tenant-margins?period=${marginPeriod}`, { headers });
      setMargins(res.data);
    } catch (err) {
      toast.error('Failed to load tenant margins');
    }
  };


  const fetchChannelAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/admin/channel-analytics`, { headers });
      setChannelAnalytics(res.data);
    } catch (err) {
      toast.error('Failed to load channel analytics');
    }
  };

  const fetchAdminOrders = async () => {
    try {
      const params = new URLSearchParams({ page: ordersPage, limit: 15 });
      if (orderStatusFilter !== 'all') params.append('status', orderStatusFilter);
      const res = await axios.get(`${API}/admin/orders?${params}`, { headers });
      setAdminOrders(res.data);
    } catch (err) {
      toast.error('Failed to load orders');
    }
  };

  const fetchSystem = async () => {
    try {
      const res = await axios.get(`${API}/admin/system`, { headers });
      setSystemData(res.data);
    } catch (err) {
      if (err.response?.status === 401) { handleAdminLogout(); return; }
      toast.error('Failed to load system data');
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${API}/admin/logs?log_type=${logType}&limit=30`, { headers });
      setSystemLogs(res.data.logs || []);
    } catch (err) {
      toast.error('Failed to load logs');
    }
  };

  const fetchContent = async () => {
    try {
      const [landing, seo, blog] = await Promise.all([
        axios.get(`${API}/admin/content/landing`, { headers }),
        axios.get(`${API}/admin/content/seo`, { headers }),
        axios.get(`${API}/admin/content/blog`, { headers })
      ]);
      setContentData({ landing: landing.data.content, seo: seo.data.seo, blog: blog.data });
    } catch (err) {
      toast.error('Failed to load content');
    }
  };

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API}/tickets/all`, { headers });
      setTicketsData(res.data);
    } catch (err) {
      toast.error('Failed to load tickets');
    }
  };

  const handleTicketReply = async (ticketId) => {
    if (!ticketReply.trim() && !adminReplyImage) return;
    try {
      await axios.post(`${API}/tickets/${ticketId}/reply`, { 
        message: ticketReply.trim() || (adminReplyImage ? '[Gambar]' : ''),
        image_url: adminReplyImage || null
      }, { headers });
      toast.success('Reply sent');
      setTicketReply('');
      setAdminReplyImage('');
      setReplyingTicket(null);
      fetchTickets();
    } catch (err) {
      toast.error('Failed to send reply');
    }
  };

  const handleAdminReplyImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await axios.post(`${API}/upload/image`, formData, { headers });
      if (res.data.url) {
        setAdminReplyImage(res.data.url);
        toast.success('Image uploaded');
      }
    } catch (err) {
      toast.error('Failed to upload image');
    }
  };

  const handleTicketStatus = async (ticketId, status) => {
    try {
      await axios.put(`${API}/tickets/${ticketId}/status`, { status }, { headers });
      toast.success(`Status updated to ${status}`);
      fetchTickets();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const fetchUserDetail = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`, { headers });
      setSelectedUser(res.data);
    } catch (err) {
      toast.error('Failed to load user detail');
    }
  };

  const handlePlanChange = async (userId, plan) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/plan`, { plan }, { headers });
      toast.success(`Plan updated to ${plan}`);
      fetchUsers();
      if (selectedUser) fetchUserDetail(userId);
    } catch (err) {
      toast.error('Failed to update plan');
    }
  };

  const handleSuspend = async (userId, suspended) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/suspend`, { suspended }, { headers });
      toast.success(suspended ? 'User suspended' : 'User unsuspended');
      fetchUsers();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const handleExtendTrial = async (userId, days = 30) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/extend-trial`, { days }, { headers });
      toast.success(`Trial extended by ${days} days`);
      fetchUsers();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const d = dashboard;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A]">
      {/* Header */}
      <header className="sticky top-0 backdrop-blur-xl bg-white/85 dark:bg-[#0A0A0A]/85 border-b border-slate-200 dark:border-zinc-800/80 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/dashboard"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4" /></Button></a>
            <h1 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-600 dark:text-indigo-400" /> Admin Panel
            </h1>
            {adminUsername && <span data-testid="admin-current-username" className="text-xs text-slate-500 hidden sm:block dark:text-zinc-400">@{adminUsername}</span>}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1 overflow-x-auto">
            {[
              { id: 'dashboard', label: 'Dashboard' },
              { id: 'users', label: 'Users' },
              { id: 'revenue', label: 'Revenue' },
              { id: 'ai', label: 'AI' },
              { id: 'margin', label: 'Margin' },
              { id: 'channels', label: 'Channels' },
              { id: 'orders', label: 'Orders' },
              { id: 'system', label: 'System' },
              { id: 'content', label: 'Content' },
              { id: 'tickets', label: 'Tickets' }
            ].map(t => (
              <button key={t.id} onClick={() => { setTab(t.id); setSelectedUser(null); }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${tab === t.id ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100 dark:text-zinc-400 dark:hover:bg-zinc-800'} dark:bg-zinc-800`}>
                {t.label}
              </button>
            ))}
            </div>
            <Button variant="ghost" size="sm" onClick={handleAdminLogout} className="text-red-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 ml-2 dark:text-red-400 dark:bg-red-950/40">
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        
        {/* ========== DASHBOARD TAB ========== */}
        {tab === 'dashboard' && d && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <StatCard icon={Users} label="Total Users" value={d.users.total} sub={`+${d.users.new_today} today`} color="blue" />
              <StatCard icon={Crown} label="Paying Users" value={d.users.paying} sub={`Trial: ${d.users.trial}`} color="purple" />
              <StatCard icon={DollarSign} label="MRR" value={`Rp ${(d.revenue.mrr/1000).toFixed(0)}k`} sub={d.revenue.gross_margin_idr !== undefined ? `Margin: Rp ${(d.revenue.gross_margin_idr/1000).toFixed(0)}k (${100 - (d.revenue.ai_cost_ratio_pct || 0)}%)` : null} color="green" />
              <StatCard icon={MessageSquare} label="Conversations" value={d.conversations.total} sub={`+${d.conversations.today} today`} color="amber" />
              <StatCard icon={Package} label="Orders" value={d.orders.total} sub={`+${d.orders.today} today`} color="pink" />
              <StatCard
                data-testid="ai-cost-stat-card"
                icon={Zap}
                label={d.ai_cost.source === 'actual' ? 'AI Cost (real)' : 'AI Cost (est)'}
                value={`Rp ${((d.ai_cost.effective_idr ?? d.ai_cost.estimated_idr)/1000).toFixed(0)}k`}
                sub={d.ai_cost.source === 'actual'
                  ? `${d.ai_cost.actual.requests} calls × Rp ${d.ai_cost.actual.idr_per_response}`
                  : `${d.ai_cost.responses_month} resp × Rp ${d.ai_cost.idr_per_response || 71} (est)`}
                color="red" />
            </div>

            {d.ai_cost.source === 'actual' && (
              <div data-testid="ai-cost-real-panel" className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5">
                <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-4 flex items-center gap-2"><Zap className="w-4 h-4" /> AI Token Usage (measured)</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 text-sm">
                  {[
                    { k: 'Avg input tokens', v: d.ai_cost.actual.avg_prompt_tokens, id: 'avg-prompt-tokens' },
                    { k: 'Avg output tokens', v: d.ai_cost.actual.avg_completion_tokens, id: 'avg-completion-tokens' },
                    { k: 'Cache hit', v: `${d.ai_cost.actual.cache_hit_pct}%`, id: 'cache-hit-pct' },
                    { k: 'Lite prompt share', v: `${d.ai_cost.actual.lite_share_pct}%`, id: 'lite-share-pct' },
                    { k: 'Est. (old formula)', v: `Rp ${d.ai_cost.idr_per_response}`, id: 'estimate-compare' },
                  ].map(x => (
                    <div key={x.id} data-testid={`ai-usage-${x.id}`}>
                      <div className="text-slate-500 dark:text-zinc-400 text-xs">{x.k}</div>
                      <div className="font-semibold text-slate-900 dark:text-zinc-50 text-lg">{x.v ?? '-'}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Plan Distribution */}
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5">
                <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Plan Distribution</h3>
                {[
                  { plan: 'Trial', count: d.users.trial, color: 'bg-slate-400' },
                  { plan: 'Starter', count: d.users.starter, color: 'bg-indigo-500' },
                  { plan: 'Growth', count: d.users.growth, color: 'bg-violet-500' },
                  { plan: 'Business', count: d.users.business, color: 'bg-amber-500' },
                ].map(p => (
                  <div key={p.plan} className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${p.color}`} />
                      <span className="text-sm text-slate-700 dark:text-zinc-300">{p.plan}</span>
                    </div>
                    <span className="text-sm font-bold text-slate-900 dark:text-zinc-50">{p.count}</span>
                  </div>
                ))}
              </div>

              {/* Channel Connections */}
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5">
                <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-4">Channel Connections</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between"><div className="flex items-center gap-2"><FaWhatsapp className="text-green-500" /><span className="text-sm">WA Shared</span></div><span className="font-bold">{d.channels.whatsapp_shared}</span></div>
                  <div className="flex items-center justify-between"><div className="flex items-center gap-2"><FaWhatsapp className="text-green-600 dark:text-green-400" /><span className="text-sm">WA Cloud API</span></div><span className="font-bold">{d.channels.whatsapp_cloud}</span></div>
                  <div className="flex items-center justify-between"><div className="flex items-center gap-2"><FaInstagram className="text-pink-500" /><span className="text-sm">Instagram</span></div><span className="font-bold">{d.channels.instagram}</span></div>
                  <div className="flex items-center justify-between"><div className="flex items-center gap-2"><FaFacebookMessenger className="text-blue-500" /><span className="text-sm">Messenger</span></div><span className="font-bold">{d.channels.messenger || 0}</span></div>
                  <div className="flex items-center justify-between"><div className="flex items-center gap-2"><span className="text-sm font-bold">T</span><span className="text-sm">TikTok Shop</span></div><span className="font-bold">{d.channels.tiktok || 0}</span></div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5">
                <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-4">Quick Stats</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500 dark:text-zinc-400">Total Messages</span><span className="font-bold">{d.messages.total}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500 dark:text-zinc-400">Messages Today</span><span className="font-bold">{d.messages.today}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500 dark:text-zinc-400">AI Responses Today</span><span className="font-bold">{d.messages.ai_today}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500 dark:text-zinc-400">Chatbots</span><span className="font-bold">{d.chatbots}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500 dark:text-zinc-400">Products</span><span className="font-bold">{d.products}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500 dark:text-zinc-400">New Users (Week)</span><span className="font-bold">{d.users.new_week}</span></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== USERS TAB ========== */}
        {tab === 'users' && !selectedUser && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-400" />
                <Input placeholder="Search users..." value={userSearch} onChange={e => setUserSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && fetchUsers()} className="pl-10" />
              </div>
              <select value={userPlanFilter} onChange={e => { setUserPlanFilter(e.target.value); setUsersPage(1); }}
                className="px-3 py-2 rounded-lg border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm">
                <option value="all">All Plans</option>
                <option value="trial">Trial</option>
                <option value="starter">Starter</option>
                <option value="growth">Growth</option>
                <option value="business">Business</option>
              </select>
              <span className="text-sm text-slate-500 dark:text-zinc-400">{usersTotal} users</span>
            </div>

            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-zinc-800/50">
                    <tr>
                      <th className="text-left p-3 font-medium text-slate-600">User</th>
                      <th className="text-left p-3 font-medium text-slate-600">Store</th>
                      <th className="text-center p-3 font-medium text-slate-600">Plan</th>
                      <th className="text-center p-3 font-medium text-slate-600">Chats</th>
                      <th className="text-center p-3 font-medium text-slate-600">Orders</th>
                      <th className="text-right p-3 font-medium text-slate-600">AI Cost</th>
                      <th className="text-right p-3 font-medium text-slate-600">Margin</th>
                      <th className="text-center p-3 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-zinc-800">
                    {users.map(u => (
                      <tr key={u.id} className={`hover:bg-slate-50 dark:hover:bg-zinc-800/30 ${u.stats?.is_cost_leaker ? 'bg-red-50 dark:bg-red-900/10' : ''}`} data-testid={`admin-user-row-${u.id}`}>
                        <td className="p-3">
                          <p className="font-medium text-slate-900 dark:text-zinc-50">{u.name || '-'}</p>
                          <p className="text-xs text-slate-500 dark:text-zinc-400">{u.email}</p>
                        </td>
                        <td className="p-3 text-slate-700 dark:text-zinc-300">{u.store_name || '-'}</td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            u.plan === 'trial' ? 'bg-slate-100 text-slate-700' :
                            u.plan === 'starter' ? 'bg-indigo-100 text-indigo-700' :
                            u.plan === 'growth' ? 'bg-violet-100 text-violet-700' :
                            'bg-amber-100 text-amber-700'
                          } dark:text-zinc-200 dark:bg-zinc-800`}>{u.plan}</span>
                        </td>
                        <td className="p-3 text-center">{u.stats?.chat_count || 0}</td>
                        <td className="p-3 text-center">{u.stats?.orders || 0}</td>
                        <td className="p-3 text-right tabular-nums text-slate-700 dark:text-zinc-300" data-testid={`admin-user-cost-${u.id}`}>
                          Rp {((u.stats?.ai_cost_idr || 0)/1000).toFixed(1)}k
                        </td>
                        <td className="p-3 text-right tabular-nums">
                          {u.stats?.is_cost_leaker ? (
                            <span className="text-red-600 dark:text-red-400 font-semibold" title="AI cost exceeds plan price">
                              -Rp {(Math.abs(u.stats.margin_idr)/1000).toFixed(1)}k
                            </span>
                          ) : u.plan === 'trial' ? (
                            <span className="text-slate-500 dark:text-zinc-400">—</span>
                          ) : (
                            <span className="text-emerald-600 dark:text-emerald-400">
                              Rp {((u.stats?.margin_idr || 0)/1000).toFixed(1)}k
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          <Button size="sm" variant="ghost" onClick={() => fetchUserDetail(u.id)} data-testid={`admin-user-view-${u.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between p-3 border-t border-slate-100 dark:border-zinc-800">
                <Button size="sm" variant="outline" disabled={usersPage <= 1} onClick={() => setUsersPage(p => p - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-500 dark:text-zinc-400">Page {usersPage}</span>
                <Button size="sm" variant="outline" disabled={users.length < 15} onClick={() => setUsersPage(p => p + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* User Detail */}
        {tab === 'users' && selectedUser && (
          <div className="space-y-4">
            <Button variant="ghost" onClick={() => setSelectedUser(null)} className="gap-2"><ArrowLeft className="w-4 h-4" /> Back</Button>
            
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50">{selectedUser.user?.name || '-'}</h2>
                  <p className="text-slate-500 dark:text-zinc-400">{selectedUser.user?.email}</p>
                  <p className="text-sm text-slate-500 dark:text-zinc-400">Store: {selectedUser.user?.store_name || '-'} | Plan: {selectedUser.user?.plan}</p>
                </div>
                <div className="flex gap-2">
                  <select onChange={e => handlePlanChange(selectedUser.user.id, e.target.value)} value={selectedUser.user?.plan}
                    className="px-3 py-1.5 rounded-lg border text-sm bg-white dark:bg-zinc-800">
                    {['trial','starter','growth','business'].map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <Button size="sm" variant="outline" onClick={() => handleExtendTrial(selectedUser.user.id)}>+30 Days Trial</Button>
                  <Button size="sm" variant="outline" className="text-red-600 dark:text-red-400" 
                    onClick={() => handleSuspend(selectedUser.user.id, !selectedUser.user.is_suspended)}>
                    {selectedUser.user?.is_suspended ? 'Unsuspend' : 'Suspend'}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="p-3 bg-slate-50 dark:bg-zinc-800/50 rounded-lg text-center">
                  <p className="text-2xl font-bold">{selectedUser.total_conversations}</p>
                  <p className="text-xs text-slate-500 dark:text-zinc-400">Conversations</p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-zinc-800/50 rounded-lg text-center">
                  <p className="text-2xl font-bold">{selectedUser.orders?.length || 0}</p>
                  <p className="text-xs text-slate-500 dark:text-zinc-400">Orders</p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-zinc-800/50 rounded-lg text-center">
                  <p className="text-2xl font-bold">{selectedUser.products?.length || 0}</p>
                  <p className="text-xs text-slate-500 dark:text-zinc-400">Products</p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-zinc-800/50 rounded-lg text-center">
                  <p className="text-2xl font-bold">{selectedUser.chatbots?.length || 0}</p>
                  <p className="text-xs text-slate-500 dark:text-zinc-400">AI Agents</p>
                </div>
              </div>

              {/* Channels */}
              <h3 className="font-semibold mb-2">Channels</h3>
              <div className="flex gap-2 mb-4 flex-wrap">
                {selectedUser.channels?.whatsapp_shared && <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs dark:text-green-300 dark:bg-green-950/40">WA Shared</span>}
                {selectedUser.channels?.whatsapp_cloud && <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs dark:text-green-300 dark:bg-green-950/40">WA Cloud API</span>}
                {selectedUser.channels?.instagram && <span className="px-2 py-1 rounded-full bg-pink-100 text-pink-700 text-xs dark:text-pink-300 dark:bg-pink-950/40">Instagram</span>}
                {selectedUser.channels?.messenger && <span className="px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs dark:text-indigo-300 dark:bg-indigo-950/40">Messenger ({selectedUser.channels.messenger.page_name || 'Page'})</span>}
                {selectedUser.channels?.tiktok && <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 text-xs dark:text-zinc-200 dark:bg-zinc-800">TikTok Shop</span>}
                {!selectedUser.channels?.whatsapp_shared && !selectedUser.channels?.whatsapp_cloud && !selectedUser.channels?.instagram && !selectedUser.channels?.messenger && !selectedUser.channels?.tiktok && <span className="text-xs text-slate-500 dark:text-zinc-400">No channels connected</span>}
              </div>

              {/* Recent Orders */}
              {selectedUser.orders?.length > 0 && (
                <>
                  <h3 className="font-semibold mb-2">Recent Orders</h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedUser.orders.slice(0, 5).map((o, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-zinc-800/50 rounded-lg text-sm">
                        <div>
                          <span className="font-medium">{o.product || 'Order'}</span>
                          <span className="text-slate-500 ml-2 dark:text-zinc-400">{o.customer_name}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-medium">Rp {(o.total || 0).toLocaleString('id-ID')}</span>
                          <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${o.status === 'Paid' || o.status === 'Sudah Bayar' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'} dark:text-green-300 dark:bg-green-950/40`}>{o.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ========== REVENUE TAB ========== */}
        {tab === 'revenue' && revenue && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <StatCard icon={DollarSign} label="MRR (potensi)" value={`Rp ${(revenue.mrr/1000).toFixed(0)}k`} sub="dari paket aktif" color="green" />
              <StatCard icon={TrendingUp} label="ARR (potensi)" value={`Rp ${((revenue.arr || 0)/1000000).toFixed(1)}jt`} color="purple" />
              <StatCard icon={DollarSign} label="Uang Masuk (total)" value={`Rp ${((revenue.realized?.total || 0)/1000).toFixed(0)}k`} sub={`${revenue.realized?.subscriptions?.count || 0} langganan · ${revenue.realized?.topups?.count || 0} top-up`} color="blue" />
              <StatCard icon={DollarSign} label="Uang Masuk (bulan ini)" value={`Rp ${((revenue.realized?.month || 0)/1000).toFixed(0)}k`} color="amber" />
              <StatCard icon={TrendingUp} label="Conversion Rate" value={`${revenue.conversion_rate}%`} sub="Trial → Paid" color="pink" />
              <StatCard icon={Users} label="Paying Users" value={revenue.paying_users ?? 0} sub={`dari ${revenue.total_users ?? 0} user`} color="blue" />
            </div>

            <div data-testid="revenue-realized-breakdown" className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5">
              <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-3">Rincian Uang Masuk</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div><span className="text-slate-500 block dark:text-zinc-400">Langganan (total)</span><span className="font-bold text-lg">Rp {(revenue.realized?.subscriptions?.total || 0).toLocaleString('id-ID')}</span></div>
                <div><span className="text-slate-500 block dark:text-zinc-400">Langganan (bulan ini)</span><span className="font-bold text-lg">Rp {(revenue.realized?.subscriptions?.month || 0).toLocaleString('id-ID')}</span></div>
                <div><span className="text-slate-500 block dark:text-zinc-400">Kredit Pay As You Go (total)</span><span className="font-bold text-lg">Rp {(revenue.realized?.topups?.total || 0).toLocaleString('id-ID')}</span></div>
                <div><span className="text-slate-500 block dark:text-zinc-400">Pembayaran belum lunas</span><span className="font-bold text-lg">{(revenue.pending_payments?.subscriptions || 0) + (revenue.pending_payments?.topups || 0)}</span></div>
              </div>
              <p className="text-xs text-slate-500 mt-3 dark:text-zinc-400">{revenue.note}</p>
            </div>

            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5">
              <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-4">Plan Distribution</h3>
              <div className="space-y-3">
                {Object.entries(revenue.plan_distribution).map(([plan, count]) => (
                  <div key={plan} className="flex items-center gap-3">
                    <span className="w-20 text-sm text-slate-600 capitalize">{plan}</span>
                    <div className="flex-1 bg-slate-100 dark:bg-zinc-800 rounded-full h-6 overflow-hidden">
                      <div className={`h-full rounded-full flex items-center px-2 text-xs text-white font-medium ${
                        plan === 'trial' ? 'bg-slate-400' : plan === 'starter' ? 'bg-indigo-500' : plan === 'growth' ? 'bg-violet-500' : 'bg-amber-500'
                      }`} style={{ width: `${Math.max((count / Math.max(Object.values(revenue.plan_distribution).reduce((a, b) => a + b, 0), 1)) * 100, 10)}%` }}>
                        {count}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {revenue.recent_payments?.length > 0 && (
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5">
                <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-4">Pembayaran Lunas Terbaru</h3>
                <div className="space-y-2">
                  {revenue.recent_payments.map((p, i) => (
                    <div key={i} data-testid={`revenue-payment-${i}`} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-zinc-800/50 rounded-lg text-sm gap-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0 ${p.kind === 'topup' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300' : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300'}`}>
                        {p.kind === 'topup' ? 'Kredit' : 'Paket'}
                      </span>
                      <span className="truncate flex-1">{p.user_email || p.reference || '-'}</span>
                      <span className="text-xs text-slate-500 dark:text-zinc-400 hidden sm:block">{p.created_at ? new Date(p.created_at).toLocaleDateString('id-ID') : ''}</span>
                      <span className="font-bold text-green-600 dark:text-green-400 shrink-0">Rp {(p.amount || 0).toLocaleString('id-ID')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ========== AI MONITORING TAB ========== */}
        {tab === 'ai' && aiMonitoring && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard icon={Zap} label="AI Today" value={aiMonitoring.totals.today} color="purple" />
              <StatCard icon={Zap} label="AI This Week" value={aiMonitoring.totals.week} color="blue" />
              <StatCard icon={Zap} label="AI This Month" value={aiMonitoring.totals.month} color="amber" />
              <StatCard
                data-testid="ai-monitoring-cost-card"
                icon={DollarSign}
                label={aiMonitoring.cost.source === 'actual' ? 'Cost (Month, real)' : 'Cost (Month, est)'}
                value={`Rp ${(aiMonitoring.cost.idr/1000).toFixed(0)}k`}
                sub={`Rp ${aiMonitoring.cost.idr_per_response}/respons${aiMonitoring.cost.avg_prompt_tokens ? ` · ${aiMonitoring.cost.avg_prompt_tokens} tok in` : ''}`}
                color="red" />
            </div>
            <p className="text-xs text-slate-500 dark:text-zinc-400">
              Sumber angka: {aiMonitoring.source === 'llm_usage' ? 'log pemakaian token nyata (llm_usage)' : 'pesan tersimpan (fallback)'}
              {aiMonitoring.cost.simple_share_pct !== null && aiMonitoring.cost.simple_share_pct !== undefined
                ? ` · prompt hemat (tier simple): ${aiMonitoring.cost.simple_share_pct}%` : ''}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Daily AI Chart */}
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5">
                <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-4">AI Responses (7 Days)</h3>
                <div className="space-y-2">
                  {aiMonitoring.daily_stats.map((d, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="w-12 text-xs text-slate-500 dark:text-zinc-400">{d.date}</span>
                      <div className="flex-1 bg-slate-100 dark:bg-zinc-800 rounded-full h-5 overflow-hidden">
                        <div className="h-full bg-violet-500 rounded-full flex items-center px-2 text-xs text-white"
                          style={{ width: `${Math.max((d.count / Math.max(...aiMonitoring.daily_stats.map(s => s.count), 1)) * 100, 5)}%` }}>
                          {d.count}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* AI Per Channel */}
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5">
                <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-4">AI Responses by Channel (bulan ini)</h3>
                <div className="space-y-3">
                  {Object.entries(aiMonitoring.per_channel).map(([ch, count]) => (
                    <div key={ch} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {ch === 'whatsapp' && <FaWhatsapp className="text-green-500" />}
                        {ch === 'instagram' && <FaInstagram className="text-pink-500" />}
                        {ch === 'messenger' && <FaFacebookMessenger className="text-blue-500" />}
                        {ch === 'tiktok' && <span className="font-bold text-sm">T</span>}
                        <span className="text-sm capitalize">{ch}</span>
                      </div>
                      <span className="font-bold">{count}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100 dark:border-zinc-800 space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-slate-500 dark:text-zinc-400">Follow-ups Sent</span><span className="font-bold">{aiMonitoring.follow_up.sent}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500 dark:text-zinc-400">Follow-ups Converted</span><span className="font-bold text-green-600 dark:text-green-400">{aiMonitoring.follow_up.converted}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500 dark:text-zinc-400">Images Processed</span><span className="font-bold">{aiMonitoring.image_processed}</span></div>
                </div>
              </div>
            </div>

            {/* Top Chatbots */}
            {aiMonitoring.chatbot_stats.length > 0 && (
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5">
                <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-4">Top AI Agents (bulan ini)</h3>
                <div className="space-y-2">
                  {aiMonitoring.chatbot_stats.map((bot, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-zinc-800/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold dark:text-indigo-400 dark:bg-indigo-950/40">{i + 1}</span>
                        <div>
                          <span className="text-sm font-medium">{bot.name}</span>
                          <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${bot.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'} dark:bg-zinc-800 dark:text-green-300`}>{bot.is_active ? 'Active' : 'Inactive'}</span>
                        </div>
                      </div>
                      <span className="font-bold text-sm">{bot.total_responses} responses</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ========== MARGIN PER TENANT TAB ========== */}
        {tab === 'margin' && margins && (
          <div className="space-y-6" data-testid="admin-margin-tab">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-zinc-50">Margin per Tenant</h2>
                <p className="text-xs text-slate-500 dark:text-zinc-400">
                  Biaya AI nyata dari log token · titik impas: Starter Rp {margins.break_even?.starter}/pesan ·
                  Growth Rp {margins.break_even?.growth} · Business Rp {margins.break_even?.business}
                </p>
              </div>
              <div className="flex gap-1">
                {['month', 'week', 'all'].map(p => (
                  <button key={p} onClick={() => setMarginPeriod(p)}
                    data-testid={`margin-period-${p}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${marginPeriod === p ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-zinc-800 dark:text-zinc-400'}`}>
                    {p === 'month' ? 'Bulan ini' : p === 'week' ? '7 hari' : 'Semua'}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <StatCard icon={DollarSign} label="MRR" value={`Rp ${((margins.summary.mrr_idr || 0) / 1000).toFixed(0)}k`} color="green" />
              <StatCard icon={Zap} label="Biaya AI" value={`Rp ${((margins.summary.ai_cost_idr || 0) / 1000).toFixed(1)}k`}
                sub={`Rp ${margins.summary.idr_per_message}/pesan`} color="red" />
              <StatCard icon={TrendingUp} label="Margin Kotor"
                value={margins.summary.gross_margin_pct !== null ? `${margins.summary.gross_margin_pct}%` : '—'}
                sub={`Rp ${((margins.summary.gross_margin_idr || 0) / 1000).toFixed(0)}k`}
                color={(margins.summary.gross_margin_pct ?? 0) >= 60 ? 'green' : 'amber'} />
              <StatCard icon={Zap} label="Biaya Tugas Otomatis"
                value={`${margins.summary.system_cost_share_pct}%`}
                sub={`Rp ${((margins.summary.system_cost_idr || 0) / 1000).toFixed(1)}k follow-up/broadcast`}
                color={margins.summary.system_cost_share_pct > 20 ? 'red' : 'blue'} />
              <StatCard icon={AlertTriangle} label="Tenant Rugi / Berisiko"
                value={`${margins.summary.loss_tenants} / ${margins.summary.at_risk_tenants}`}
                sub={`dari ${margins.summary.tenants} tenant`}
                color={(margins.summary.loss_tenants + margins.summary.at_risk_tenants) > 0 ? 'red' : 'green'} />
            </div>

            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-zinc-800/50 text-xs text-slate-500 dark:text-zinc-400">
                    <tr>
                      <th className="p-3 text-left font-medium">Tenant</th>
                      <th className="p-3 text-left font-medium">Plan</th>
                      <th className="p-3 text-right font-medium">Kuota</th>
                      <th className="p-3 text-right font-medium">Biaya AI</th>
                      <th className="p-3 text-right font-medium">Rp/pesan</th>
                      <th className="p-3 text-right font-medium">Impas</th>
                      <th className="p-3 text-right font-medium">Proyeksi @kuota</th>
                      <th className="p-3 text-right font-medium">Otomatis</th>
                      <th className="p-3 text-right font-medium">Cache</th>
                      <th className="p-3 text-right font-medium">Margin</th>
                      <th className="p-3 text-center font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-zinc-800">
                    {margins.tenants.map(t => (
                      <tr key={t.id} className="hover:bg-slate-50 dark:hover:bg-zinc-800/40" data-testid={`margin-row-${t.id}`}>
                        <td className="p-3">
                          <p className="font-medium text-slate-900 dark:text-zinc-100 truncate max-w-[180px]">{t.name || '—'}</p>
                          <p className="text-xs text-slate-500 dark:text-zinc-500 truncate max-w-[180px]">{t.email}</p>
                        </td>
                        <td className="p-3 capitalize text-slate-700 dark:text-zinc-300">{t.plan}</td>
                        <td className="p-3 text-right tabular-nums text-slate-700 dark:text-zinc-300">
                          {t.chat_count}{t.quota_messages ? `/${t.quota_messages}` : ''}
                          {t.quota_used_pct !== null && <span className="block text-xs text-slate-400">{t.quota_used_pct}%</span>}
                        </td>
                        <td className="p-3 text-right tabular-nums text-slate-700 dark:text-zinc-300">Rp {t.ai_cost_idr.toLocaleString('id-ID')}</td>
                        <td className="p-3 text-right tabular-nums font-medium text-slate-900 dark:text-zinc-100">{t.idr_per_message}</td>
                        <td className="p-3 text-right tabular-nums text-slate-500 dark:text-zinc-400">{t.break_even_idr_per_message ?? '—'}</td>
                        <td className="p-3 text-right tabular-nums text-slate-700 dark:text-zinc-300">
                          {t.projected_cost_at_quota_idr ? `Rp ${t.projected_cost_at_quota_idr.toLocaleString('id-ID')}` : '—'}
                        </td>
                        <td className={`p-3 text-right tabular-nums ${t.system_cost_share_pct > 20 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-slate-500 dark:text-zinc-400'}`}>
                          {t.system_cost_share_pct}%
                        </td>
                        <td className="p-3 text-right tabular-nums text-slate-500 dark:text-zinc-400">{t.cache_hit_pct}%</td>
                        <td className={`p-3 text-right tabular-nums font-medium ${t.margin_idr < 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                          {t.margin_pct !== null ? `${t.margin_pct}%` : '—'}
                        </td>
                        <td className="p-3 text-center">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            t.status === 'loss' ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            : t.status === 'at_risk' ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                            : t.status === 'watch' ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'}`}>
                            {t.status === 'loss' ? 'Rugi' : t.status === 'at_risk' ? 'Berisiko' : t.status === 'watch' ? 'Pantau' : t.status === 'custom' ? 'Custom' : 'Sehat'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {margins.tenants.length === 0 && (
                <p className="p-6 text-center text-sm text-slate-500 dark:text-zinc-400">Belum ada pemakaian AI pada periode ini.</p>
              )}
            </div>

            <p className="text-xs text-slate-500 dark:text-zinc-400">
              <strong>Otomatis</strong> = porsi biaya dari follow-up/broadcast (channel system), bukan chat customer — kalau di atas 20% berarti ada tugas otomatis yang boros.
              <strong className="ml-2">Proyeksi @kuota</strong> = biaya AI bila tenant memakai habis kuotanya pada laju biaya saat ini; kalau melebihi harga plan, plan itu rugi.
            </p>
          </div>
        )}


        {/* ========== CHANNEL ANALYTICS TAB ========== */}
        {tab === 'channels' && channelAnalytics && (
          <div className="space-y-6">
            {/* Channel Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(channelAnalytics.channels).map(([ch, data]) => (
                <div key={ch} className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5">
                  <div className="flex items-center gap-2 mb-4">
                    {ch === 'whatsapp' && <FaWhatsapp className="text-green-500 w-5 h-5" />}
                    {ch === 'instagram' && <FaInstagram className="text-pink-500 w-5 h-5" />}
                    {ch === 'messenger' && <FaFacebookMessenger className="text-blue-500 w-5 h-5" />}
                    {ch === 'tiktok' && <span className="font-bold">TikTok</span>}
                    {ch === 'website' && <MessageSquare className="text-slate-500 w-5 h-5 dark:text-zinc-400" />}
                    <h3 className="font-semibold capitalize">{ch}</h3>
                    <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${channelAnalytics.webhook_status[ch]?.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'} dark:text-green-300 dark:bg-green-950/40`}>
                      {channelAnalytics.webhook_status[ch]?.status || 'N/A'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-slate-500 block dark:text-zinc-400">Conversations</span><span className="font-bold text-lg">{data.conversations.total}</span><span className="text-xs text-slate-500 ml-1 dark:text-zinc-400">+{data.conversations.today} today</span></div>
                    <div><span className="text-slate-500 block dark:text-zinc-400">Messages</span><span className="font-bold text-lg">{data.messages.total}</span><span className="text-xs text-slate-500 ml-1 dark:text-zinc-400">+{data.messages.month} /mo</span></div>
                    <div><span className="text-slate-500 block dark:text-zinc-400">Orders</span><span className="font-bold text-lg">{data.orders.total}</span><span className="text-xs text-slate-500 ml-1 dark:text-zinc-400">+{data.orders.month} /mo</span></div>
                    <div><span className="text-slate-500 block dark:text-zinc-400">Conv. Rate</span><span className="font-bold text-lg text-green-600 dark:text-green-400">{data.conversion_rate}%</span></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Daily Trend */}
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5">
              <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-4">Daily Conversations (7 Days)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-slate-500 border-b dark:text-zinc-400">
                      <th className="text-left p-2">Date</th>
                      <th className="text-center p-2">WhatsApp</th>
                      <th className="text-center p-2">Instagram</th>
                      <th className="text-center p-2">Messenger</th>
                      <th className="text-center p-2">TikTok</th>
                      <th className="text-center p-2">Website</th>
                    </tr>
                  </thead>
                  <tbody>
                    {channelAnalytics.daily_trend.map((d, i) => (
                      <tr key={i} className="border-b border-slate-50 dark:border-zinc-800">
                        <td className="p-2 font-medium">{d.date}</td>
                        <td className="p-2 text-center">{d.whatsapp || 0}</td>
                        <td className="p-2 text-center">{d.instagram || 0}</td>
                        <td className="p-2 text-center">{d.messenger || 0}</td>
                        <td className="p-2 text-center">{d.tiktok || 0}</td>
                        <td className="p-2 text-center">{d.website || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ========== ORDER MONITORING TAB ========== */}
        {tab === 'orders' && adminOrders && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
              <StatCard icon={Package} label="Total Orders" value={adminOrders.summary.total} sub={`+${adminOrders.summary.today} today`} color="blue" />
              <StatCard icon={Clock} label="Belum Bayar" value={adminOrders.summary.by_status.pending} color="amber" />
              <StatCard icon={Shield} label="Verifikasi" value={adminOrders.summary.by_status.verifikasi} color="amber" />
              <StatCard icon={DollarSign} label="Sudah Bayar" value={adminOrders.summary.by_status.paid} color="green" />
              <StatCard icon={ShoppingBag} label="Dikirim/Selesai" value={(adminOrders.summary.by_status.shipped || 0) + (adminOrders.summary.by_status.completed || 0)} color="purple" />
              <StatCard icon={DollarSign} label="Revenue (Month)" value={`Rp ${(adminOrders.summary.revenue.month / 1000).toFixed(0)}k`} sub={`Total: Rp ${((adminOrders.summary.revenue.total || 0)/1000).toFixed(0)}k`} color="green" />
              <StatCard icon={TrendingUp} label="GMV / Belum Lunas" value={`Rp ${((adminOrders.summary.gmv || 0)/1000).toFixed(0)}k`} sub={`Nyangkut: Rp ${((adminOrders.summary.open_value || 0)/1000).toFixed(0)}k`} color="pink" />
            </div>

            {/* Filter */}
            <div className="flex gap-3 items-center">
              <select data-testid="orders-status-filter" value={orderStatusFilter} onChange={e => { setOrderStatusFilter(e.target.value); setOrdersPage(1); }}
                className="px-3 py-2 rounded-lg border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm">
                <option value="all">Semua Status</option>
                <option value="pending">Belum Bayar</option>
                <option value="verifikasi">Menunggu Verifikasi</option>
                <option value="paid">Sudah Bayar</option>
                <option value="shipped">Dikirim</option>
                <option value="completed">Selesai</option>
                <option value="cancelled">Dibatalkan</option>
              </select>
              <span className="text-sm text-slate-500 dark:text-zinc-400">{adminOrders.total} orders</span>
              {adminOrders.summary.by_status.other > 0 && (
                <span className="text-xs text-amber-600 dark:text-amber-400">{adminOrders.summary.by_status.other} pesanan status lain</span>
              )}
            </div>

            {/* Orders Table */}
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-zinc-800/50">
                    <tr>
                      <th className="text-left p-3 font-medium text-slate-600">Order</th>
                      <th className="text-left p-3 font-medium text-slate-600">Store</th>
                      <th className="text-left p-3 font-medium text-slate-600">Customer</th>
                      <th className="text-left p-3 font-medium text-slate-600">Product</th>
                      <th className="text-right p-3 font-medium text-slate-600">Total</th>
                      <th className="text-center p-3 font-medium text-slate-600">Channel</th>
                      <th className="text-center p-3 font-medium text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-zinc-800">
                    {adminOrders.orders.map((o, i) => (
                      <tr key={i} className="hover:bg-slate-50 dark:hover:bg-zinc-800/30 dark:bg-zinc-900">
                        <td className="p-3">
                          <p className="font-medium text-xs">{o.order_id || o.id?.slice(0, 8)}</p>
                          <p className="text-xs text-slate-500 dark:text-zinc-400">{o.created_at ? new Date(o.created_at).toLocaleDateString('id-ID') : '-'}</p>
                        </td>
                        <td className="p-3 text-slate-700 dark:text-zinc-300 text-xs">{o.store_name || '-'}</td>
                        <td className="p-3 text-xs">{o.customer_name || '-'}</td>
                        <td className="p-3 text-xs">{o.product || '-'}</td>
                        <td className="p-3 text-right font-medium text-xs">Rp {(o.total || 0).toLocaleString('id-ID')}</td>
                        <td className="p-3 text-center">
                          <span className="text-xs capitalize">{o.channel || '-'}</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            ['Paid','Sudah Bayar'].includes(o.status) ? 'bg-green-100 text-green-700' :
                            ['Shipped','Dikirim'].includes(o.status) ? 'bg-indigo-100 text-indigo-700' :
                            ['Completed','Selesai'].includes(o.status) ? 'bg-emerald-100 text-emerald-700' :
                            'bg-yellow-100 text-yellow-700'
                          } dark:text-green-300 dark:bg-green-950/40`}>{o.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between p-3 border-t border-slate-100 dark:border-zinc-800">
                <Button size="sm" variant="outline" disabled={ordersPage <= 1} onClick={() => setOrdersPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                <span className="text-sm text-slate-500 dark:text-zinc-400">Page {ordersPage} of {adminOrders.pages}</span>
                <Button size="sm" variant="outline" disabled={adminOrders.orders.length < 15} onClick={() => setOrdersPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        )}

        {/* ========== SYSTEM & LOGS TAB ========== */}
        {tab === 'system' && systemData && (
          <div className="space-y-6">
            {/* DB Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard icon={BarChart3} label="DB Storage" value={`${systemData.database.storage_mb} MB`} sub={`${systemData.database.storage_pct}% of ${systemData.database.storage_limit_mb} MB`} color={systemData.database.storage_pct > 80 ? "red" : "blue"} />
              <StatCard icon={Package} label="Collections" value={systemData.database.total_collections} color="purple" />
              <StatCard icon={Shield} label="Admin Sessions" value={systemData.admin_sessions} color="amber" />
              <StatCard icon={Zap} label="Schedulers" value={Object.values(systemData.schedulers).filter(s => s === 'active').length + ' active'} color="green" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Collection Sizes */}
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5">
                <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-4">Collection Sizes</h3>
                <div className="space-y-1.5 max-h-80 overflow-y-auto">
                  {systemData.database.collections.map((c, i) => (
                    <div key={i} className="flex items-center justify-between py-1 text-sm">
                      <span className="text-slate-600 dark:text-zinc-400 font-mono text-xs">{c.name}</span>
                      <span className="font-bold text-slate-900 dark:text-zinc-50">{c.count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Schedulers */}
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5">
                <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-4">Background Schedulers</h3>
                <div className="space-y-3">
                  {Object.entries(systemData.schedulers).map(([name, status]) => (
                    <div key={name} className="flex items-center justify-between">
                      <span className="text-sm capitalize">{name.replace(/_/g, ' ')}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} dark:text-green-300 dark:bg-green-950/40`}>{status}</span>
                    </div>
                  ))}
                </div>

                <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-3 mt-6">Webhook Messages</h3>
                <div className="space-y-2 text-sm">
                  {Object.entries(systemData.webhook_messages).map(([ch, count]) => (
                    <div key={ch} className="flex justify-between">
                      <span className="text-slate-500 capitalize dark:text-zinc-400">{ch}</span>
                      <span className="font-bold">{count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Logs Viewer */}
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900 dark:text-zinc-50">Logs</h3>
                <select value={logType} onChange={e => setLogType(e.target.value)}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-800 text-sm">
                  <option value="activity">Admin Activity</option>
                  <option value="follow_up">Follow-Up</option>
                  <option value="payments">Payments</option>
                  <option value="webhooks">Webhook Logs</option>
                  <option value="image">Image Processing</option>
                  <option value="booking">Booking Debug</option>
                </select>
              </div>
              <div className="space-y-1 max-h-96 overflow-y-auto font-mono text-xs">
                {systemLogs.length === 0 && <p className="text-slate-500 py-4 text-center dark:text-zinc-400">No logs</p>}
                {systemLogs.map((log, i) => (
                  <div key={i} className="p-2 bg-slate-50 dark:bg-[#0A0A0A] rounded border border-slate-100 dark:border-zinc-800">
                    <span className="text-slate-500 dark:text-zinc-400">{log.timestamp || log.sent_at || log.created_at || '-'}</span>
                    <pre className="text-slate-700 dark:text-zinc-300 mt-1 whitespace-pre-wrap break-all text-[10px]">
                      {typeof log === 'string' ? log : JSON.stringify(log, null, 1).substring(0, 500)}
                    </pre>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Follow-Up Logs */}
            {systemData.follow_up_logs?.length > 0 && (
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5">
                <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-4">Recent Follow-Up Activity</h3>
                <div className="space-y-2">
                  {systemData.follow_up_logs.map((f, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-zinc-800/50 rounded-lg text-sm">
                      <div>
                        <span className="font-medium">{f.customer_name || '-'}</span>
                        <span className="text-slate-500 ml-2 text-xs dark:text-zinc-400">{f.type} • {f.channel}</span>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs ${f.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} dark:text-green-300 dark:bg-green-950/40`}>{f.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ========== CONTENT MANAGEMENT TAB ========== */}
        {tab === 'content' && contentData && (
          <div className="space-y-6">
            {/* Blog Overview */}
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5">
              <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-4 flex items-center gap-2">
                <Package className="w-4 h-4" /> Blog Articles
              </h3>
              <p className="text-sm text-slate-500 mb-4 dark:text-zinc-400">{contentData.blog.total} articles published</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {contentData.blog.categories?.map((cat, i) => (
                  <div key={i} className="p-3 bg-slate-50 dark:bg-zinc-800/50 rounded-lg">
                    <p className="text-xs text-slate-500 dark:text-zinc-400">{cat.name}</p>
                    <p className="text-lg font-bold text-slate-900 dark:text-zinc-50">{cat.count}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-3 dark:text-zinc-400">{contentData.blog.note}</p>
            </div>

            {/* Landing Page Content */}
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5">
              <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-4 flex items-center gap-2">
                <Eye className="w-4 h-4" /> Landing Page
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500 block mb-1 dark:text-zinc-400">Hero Title</label>
                  <p className="text-sm font-medium text-slate-900 dark:text-zinc-50 bg-slate-50 dark:bg-zinc-800/50 p-2 rounded">{contentData.landing?.hero_title || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1 dark:text-zinc-400">Hero Subtitle</label>
                  <p className="text-sm text-slate-700 dark:text-zinc-300 bg-slate-50 dark:bg-zinc-800/50 p-2 rounded">{contentData.landing?.hero_subtitle || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1 dark:text-zinc-400">CTA Text</label>
                  <p className="text-sm text-slate-700 dark:text-zinc-300 bg-slate-50 dark:bg-zinc-800/50 p-2 rounded">{contentData.landing?.cta_text || '-'}</p>
                </div>
              </div>
            </div>

            {/* SEO Settings */}
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5">
              <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-4 flex items-center gap-2">
                <Search className="w-4 h-4" /> SEO Settings
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500 block mb-1 dark:text-zinc-400">Page Title</label>
                  <p className="text-sm text-slate-900 dark:text-zinc-50 bg-slate-50 dark:bg-zinc-800/50 p-2 rounded">{contentData.seo?.title || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1 dark:text-zinc-400">Meta Description</label>
                  <p className="text-sm text-slate-700 dark:text-zinc-300 bg-slate-50 dark:bg-zinc-800/50 p-2 rounded">{contentData.seo?.description || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1 dark:text-zinc-400">Keywords</label>
                  <p className="text-sm text-slate-700 dark:text-zinc-300 bg-slate-50 dark:bg-zinc-800/50 p-2 rounded">{contentData.seo?.keywords || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1 dark:text-zinc-400">OG Image</label>
                  <p className="text-sm text-slate-700 dark:text-zinc-300 bg-slate-50 dark:bg-zinc-800/50 p-2 rounded">{contentData.seo?.og_image || '-'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== TICKETS TAB ========== */}
        {tab === 'tickets' && ticketsData && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard icon={MessageSquare} label="Open" value={ticketsData.stats?.open || 0} color="amber" />
              <StatCard icon={Clock} label="In Progress" value={ticketsData.stats?.in_progress || 0} color="blue" />
              <StatCard icon={Bot} label="Resolved" value={ticketsData.stats?.resolved || 0} color="green" />
              <StatCard icon={Users} label="Total" value={ticketsData.stats?.total || 0} color="purple" />
            </div>

            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 overflow-hidden">
              {ticketsData.tickets?.length === 0 ? (
                <div className="p-8 text-center text-slate-500 dark:text-zinc-400">No tickets yet</div>
              ) : (
                <div className="divide-y divide-slate-100 dark:divide-zinc-800">
                  {ticketsData.tickets.map(ticket => (
                    <div key={ticket.id} className="p-4">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-xs font-mono text-slate-500 dark:text-zinc-400">#{ticket.id}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              ticket.status === 'open' ? 'bg-yellow-100 text-yellow-700' :
                              ticket.status === 'in_progress' ? 'bg-indigo-100 text-indigo-700' :
                              'bg-green-100 text-green-700'
                            } dark:text-yellow-300 dark:bg-yellow-950/40`}>{ticket.status}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                              ticket.type === 'bug' ? 'bg-red-100 text-red-700' :
                              ticket.type === 'suggestion' ? 'bg-amber-100 text-amber-700' :
                              'bg-indigo-100 text-indigo-700'
                            } dark:text-red-300 dark:bg-red-950/40`}>{ticket.type}</span>
                            <span className="text-[10px] text-slate-500 dark:text-zinc-400">{ticket.user_email}</span>
                          </div>
                          <p className="text-sm text-slate-800 dark:text-zinc-200">{ticket.message}</p>
                          {ticket.screenshot_url && (
                            <img src={ticket.screenshot_url} alt="" className="mt-2 max-h-24 rounded border" />
                          )}
                          <p className="text-[10px] text-slate-500 mt-1 dark:text-zinc-400">
                            {new Date(ticket.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <select
                            value={ticket.status}
                            onChange={e => handleTicketStatus(ticket.id, e.target.value)}
                            className="text-[10px] px-1.5 py-1 rounded border bg-white dark:bg-zinc-800"
                          >
                            <option value="open">Open</option>
                            <option value="in_progress">In Progress</option>
                            <option value="resolved">Resolved</option>
                          </select>
                        </div>
                      </div>

                      {/* Replies */}
                      {ticket.replies?.length > 0 && (
                        <div className="ml-4 mt-2 space-y-1.5 border-l-2 border-indigo-200 pl-3">
                          {ticket.replies.map((r, i) => (
                            <div key={i} className={`rounded p-2 ${r.from === 'admin' ? 'bg-indigo-50 dark:bg-indigo-950/30' : 'bg-slate-50 dark:bg-zinc-800/30'}`}>
                              <p className="text-xs text-slate-700 dark:text-zinc-300">{r.message}</p>
                              {r.image_url && (
                                <img src={r.image_url} alt="Attachment" className="mt-1.5 rounded border max-h-32 object-cover cursor-pointer" onClick={() => window.open(r.image_url, '_blank')} />
                              )}
                              <p className="text-[10px] text-slate-500 mt-0.5 dark:text-zinc-400">
                                {r.from === 'admin' ? r.admin_email : (r.user_email || ticket.user_email)} 
                                <span className={`ml-1 font-medium ${r.from === 'admin' ? 'text-blue-500' : 'text-green-500'}`}>({r.from === 'admin' ? 'admin' : 'user'})</span>
                                {' • '}{new Date(r.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Reply Input */}
                      {replyingTicket === ticket.id ? (
                        <div className="mt-2 space-y-2">
                          {adminReplyImage && (
                            <div className="relative inline-block">
                              <img src={adminReplyImage} alt="Preview" className="h-20 rounded border" />
                              <button onClick={() => setAdminReplyImage('')} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]">x</button>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <input
                              value={ticketReply}
                              onChange={e => setTicketReply(e.target.value)}
                              placeholder="Type reply..."
                              className="flex-1 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-zinc-800 bg-white dark:bg-[#0A0A0A] text-sm"
                              onKeyDown={e => e.key === 'Enter' && handleTicketReply(ticket.id)}
                            />
                            <label className="px-2 py-1.5 rounded-lg border border-slate-200 dark:border-zinc-800 hover:bg-slate-50 dark:hover:bg-zinc-900 cursor-pointer flex items-center dark:bg-zinc-900">
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-slate-500 dark:text-zinc-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
                              <input type="file" accept="image/*" className="hidden" onChange={handleAdminReplyImage} />
                            </label>
                            <Button size="sm" onClick={() => handleTicketReply(ticket.id)} className="bg-indigo-600">Send</Button>
                            <Button size="sm" variant="ghost" onClick={() => { setReplyingTicket(null); setTicketReply(''); setAdminReplyImage(''); }}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setReplyingTicket(ticket.id)}
                          className="mt-2 text-xs text-indigo-600 hover:underline dark:text-indigo-400"
                        >
                          Reply
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {loading && !d && tab === 'dashboard' && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
